import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Play, X, Video, Sparkles, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { introShownThisSession, markIntroShownThisSession } from '../utils/introSession';

/**
 * WelcomeVideo Component
 * 
 * Props:
 * - videoUrl: YouTube, Vimeo, or MP4 URL (fallback if API fails)
 * - title: Card title (default: "Welcome to iEvntür")
 * - subtitle: Card subtitle
 * - showOnFirstVisit: Show modal on first visit (default: true)
 * - storageKey: localStorage key for dismissal tracking (default: 'ievntur_welcome_video_dismissed')
 * - settingsKey: Backend settings key for video URL (default: 'client_welcome_video_url')
 * - cardDescription: Description text on the video card
 * - accentColor: Color theme ('cyan' or 'purple')
 */
const WelcomeVideo = ({ 
  // Iter-337 — DEFAULT MUST BE null (not a hardcoded URL). If the admin
  // hasn't configured a welcome video, we render nothing rather than
  // showing an unrelated fallback video. Any hardcoded fallback here
  // ends up on every user's dashboard the moment the settings fetch
  // fails or the DB row is empty. See production incident 2026-07-14.
  videoUrl: fallbackUrl = null,
  title = "Welcome to iEvntür",
  subtitle = "Watch this quick walkthrough to get started",
  showOnFirstVisit = true,
  storageKey = 'ievntur_welcome_video_dismissed',
  settingsKey = 'client_welcome_video_url',
  cardDescription = "Learn how to post gigs, find talent & more",
  accentColor = 'cyan'
}) => {
  const [showModal, setShowModal] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [videoUrl, setVideoUrl] = useState(fallbackUrl);
  const [loading, setLoading] = useState(true);

  // Color theme classes
  const colorClasses = {
    cyan: {
      card: 'from-cyan-50 to-purple-50 border-cyan-200',
      icon: 'text-cyan-600',
      title: 'text-cyan-800',
      subtitle: 'text-cyan-600',
      button: 'border-cyan-300 text-cyan-700 hover:bg-cyan-50',
      gradient: 'from-cyan-600 to-purple-600',
      cta: 'bg-cyan-500 hover:bg-cyan-600'
    },
    purple: {
      card: 'from-purple-50 to-pink-50 border-purple-200',
      icon: 'text-purple-600',
      title: 'text-purple-800',
      subtitle: 'text-purple-600',
      button: 'border-purple-300 text-purple-700 hover:bg-purple-50',
      gradient: 'from-purple-600 to-pink-600',
      cta: 'bg-purple-500 hover:bg-purple-600'
    }
  };
  
  const colors = colorClasses[accentColor] || colorClasses.cyan;

  useEffect(() => {
    // Fetch video URL from backend
    const fetchVideoUrl = async () => {
      try {
        const res = await api.get(`/home/welcome-video?key=${settingsKey}`);
        if (res.data.video_url) {
          setVideoUrl(res.data.video_url);
        }
      } catch (e) {
        console.log('Using fallback video URL');
      } finally {
        setLoading(false);
      }
    };
    fetchVideoUrl();
    
    // Check if this is the first visit
    const dismissed = localStorage.getItem(storageKey);
    // If a Clock-In/Clock-Out reminder is currently showing on the dashboard,
    // suppress the auto-open of the welcome video so the big "Clock In Now"
    // button isn't covered by an onboarding modal. The reminder is a much
    // higher-priority message for non-technical staff.
    const clockReminderActive = sessionStorage.getItem('clockReminderActive') === '1';
    // A mandatory Service Agreement gate takes priority over onboarding — never
    // auto-open the welcome video while the client still has a required contract
    // to sign (it would cover / fight the blocking agreement modal).
    const agreementGateActive = window.__agreementGateBlocking === true;
    if (!dismissed && showOnFirstVisit && !clockReminderActive && !agreementGateActive && !introShownThisSession()) {
      // Small delay to let the page load first.
      // Also flip the flag the moment we schedule the open so the modal only
      // ever appears once per browser — previously repeat visitors could get
      // re-prompted and the modal blocked home CTAs (including UpgradeToProCard).
      const timer = setTimeout(() => {
        if (window.__agreementGateBlocking === true) return;
        localStorage.setItem(storageKey, 'true');
        markIntroShownThisSession();
        setShowModal(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [showOnFirstVisit, fallbackUrl, storageKey, settingsKey]);

  const handleCloseModal = () => {
    if (dontShowAgain) {
      localStorage.setItem(storageKey, 'true');
    }
    setShowModal(false);
  };

  const handleGotIt = () => {
    // Always mark as seen when they click "Got it"
    localStorage.setItem(storageKey, 'true');
    setShowModal(false);
  };

  // Parse video URL to get embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtube.com/embed/') || url.includes('youtu.be/')) {
      if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return url;
    }
    
    // Vimeo
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    
    // Direct MP4 or other - return as is
    return url;
  };

  const embedUrl = getEmbedUrl(videoUrl);
  const isDirectVideo = videoUrl?.endsWith('.mp4') || videoUrl?.endsWith('.webm');
  const hasVideo = Boolean(videoUrl && videoUrl.trim());

  const VideoPlayer = ({ className = "" }) => (
    <div className={`relative w-full aspect-video bg-black rounded-lg overflow-hidden ${className}`}>
      {isDirectVideo ? (
        <video 
          controls 
          className="w-full h-full"
          poster=""
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      ) : (
        <iframe
          src={embedUrl}
          title="Welcome Video"
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );

  // When no welcome video is configured, render nothing instead of a broken
  // black player. Admins configure videos at /admin/video-settings.
  if (!loading && !hasVideo) {
    return null;
  }

  return (
    <>
      {/* Dashboard Video Card */}
      <Card className={`bg-gradient-to-r ${colors.card}`} data-testid="welcome-video-card">
        <CardHeader className="py-3 px-4">
          <CardTitle className={`text-sm font-medium flex items-center gap-2 ${colors.title}`}>
            <Sparkles className={`w-4 h-4 ${colors.icon}`} />
            {title}
          </CardTitle>
          <p className={`text-xs mt-0.5 ${colors.subtitle}`}>{subtitle}</p>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div 
            className="relative cursor-pointer group"
            onClick={() => setShowModal(true)}
            data-testid="welcome-video-thumbnail"
          >
            <div className={`w-full aspect-video bg-gradient-to-br ${colors.gradient} rounded-lg flex items-center justify-center`}>
              <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
            </div>
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-white text-sm font-medium drop-shadow-lg">Platform Walkthrough</p>
              <p className="text-white/80 text-xs">{cardDescription}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className={`w-full mt-3 ${colors.button}`}
            onClick={() => setShowModal(true)}
          >
            <Video className="w-4 h-4 mr-2" />
            Getting Started
          </Button>
        </CardContent>
      </Card>

      {/* Welcome Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl" data-testid="welcome-video-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className={`w-5 h-5 ${colors.icon}`} />
              {title}
            </DialogTitle>
            <DialogDescription>
              {subtitle}
            </DialogDescription>
          </DialogHeader>
          
          <VideoPlayer className="my-4" />
          
          <DialogFooter className="flex-col sm:flex-row gap-3">
            <div className="flex items-center gap-2 mr-auto">
              <Checkbox 
                id="dontShowAgain" 
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
              />
              <label 
                htmlFor="dontShowAgain" 
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Don't show this again
              </label>
            </div>
            <Button 
              onClick={handleGotIt}
              className={colors.cta}
              data-testid="welcome-video-gotit-btn"
            >
              Got it, let's go!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WelcomeVideo;
